
import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Star } from 'lucide-react';
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { handleFareHarborBooking } from '@/utils/fareHarborUtils';
import { handleImageError } from '@/utils/imageHandling';

interface Tour {
  id: number;
  title: string;
  description: string;
  image: string;
  bookingUrl: string;
  featured?: boolean;
  teaser?: string;
  itemId?: number;
}

interface TourListProps {
  tours: Tour[];
}

// Helper function to get the URL slug from the tour title
const getTourSlug = (title: string): string => {
  // Map tour titles to their corresponding URL paths
  if (title.includes("Financial District")) {
    return "/tours/financial-district";
  } else if (title.includes("Brooklyn Heights")) {
    return "/tours/brooklyn-heights";
  } else if (title.includes("Lower East Side")) {
    return "/tours/lower-east-side";
  } else if (title.includes("Private Tours")) {
    return "/tours/private-tours";
  }
  // Default fallback
  return "/tours";
};

// Helper to get formatted title with consistent parenthetical information
const getFormattedTitle = (title: string): string => {
  if (title.includes("Brooklyn Heights")) {
    return "Brooklyn Heights Cat Tour (2 hours, $40)";
  } else if (title.includes("Financial District")) {
    return "Financial District Cat Tour (2 hours, $40)";
  } else if (title.includes("Lower East Side")) {
    return "Lower East Side Cat Tour (2 hours, $40)";
  } else if (title.includes("Private Tours")) {
    return "Private Tours & Special Events (Custom, Varies)";
  }
  return title;
};

// Helper to get the correct ItemId for each tour
const getItemId = (title: string): number => {
  if (title.includes("Brooklyn Heights")) {
    return 562630;
  } else if (title.includes("Financial District")) {
    return 577800;
  } else if (title.includes("Lower East Side")) {
    return 604533;
  } else if (title.includes("Private Tours")) {
    return 562633;
  }
  return 562630; // Default to Brooklyn Heights as fallback
};

const TourList: React.FC<TourListProps> = ({ tours }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      {tours.map((tour) => {
        const isPrivateTour = tour.title.includes("Private Tours");
        const formattedTitle = getFormattedTitle(tour.title);
        const itemId = tour.itemId || getItemId(tour.title);
        const detailsLink = getTourSlug(tour.title);
        
        return (
          <Card key={tour.id} className="rounded-lg overflow-hidden border border-[#C58B65] shadow-sm hover:shadow-md hover:scale-[1.02] transition-all duration-300">
            <div className="relative">
              {/* Make the image clickable with Link wrapper */}
              <Link to={detailsLink} className="block cursor-pointer">
                <img 
                  src={tour.image} 
                  alt={`${tour.title} - NYC Cat Walking Tour`} 
                  className="w-full h-[300px] object-cover transition-transform duration-500 hover:scale-105" 
                  loading="lazy"
                  width="600"
                  height="300"
                  onError={(e) => handleImageError(e, '/lovable-uploads/tour-placeholder.jpg')}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/50 opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
              </Link>
              {tour.featured && (
                <div className="absolute top-3 right-3 bg-[#D4AF37] text-white px-3 py-1 rounded-full flex items-center shadow-md">
                  <Star className="w-4 h-4 mr-1 text-white" aria-hidden="true" />
                  <span className="text-sm font-semibold">Most Popular</span>
                </div>
              )}
            </div>
            
            <CardHeader className="p-6">
              <h3 className="text-xl font-montserrat font-semibold text-[#3E2C2F]">{formattedTitle}</h3>
            </CardHeader>
            
            <CardContent className="p-6 pt-0">
              <p className="text-[#4A3F3B] font-montserrat text-sm mb-4">{tour.description}</p>
              
              {tour.teaser && (
                <div className="bg-[#F5EDE0] p-3 rounded-md border-l-4 border-[#C58B65] mb-4">
                  <p className="text-[#3E2C2F] text-sm italic font-lora">"{tour.teaser}"</p>
                </div>
              )}
            </CardContent>
            
            <CardFooter className="p-6 flex flex-col sm:flex-row justify-between pt-0 gap-4">
              <Link 
                to={detailsLink} 
                className="text-[#C46829] font-montserrat font-semibold text-sm hover:underline transition-colors"
              >
                View Details
              </Link>
              
              <div className="flex flex-col items-end w-full sm:w-auto">
                <a
                  href={tour.bookingUrl || `https://fareharbor.com/embeds/book/catsabouttown/items/${itemId}/calendar/`}
                  onClick={(e) => handleFareHarborBooking({ 
                    e, 
                    itemId: itemId 
                  })}
                  className="bg-[#C46829] text-white hover:text-white px-8 py-3 rounded font-montserrat font-semibold text-base hover:bg-[#B35718] transition-all duration-300 hover:scale-105 hover:shadow-md inline-flex items-center justify-center gap-2 w-full sm:w-auto"
                  style={{ minWidth: '200px' }}
                  data-testid={`book-tour-${tour.id}`}
                >
                  <Calendar size={18} aria-hidden="true" />
                  {isPrivateTour ? "Contact Us to Book" : "Reserve Your Spot"}
                </a>
                
                {!isPrivateTour && (
                  <div className="flex flex-col mt-2 text-right">
                    <span className="text-xs font-medium text-[#4A3F3B]/80">$40 (avg. NYC walking tours: $55)</span>
                    {tour.featured && (
                      <span className="text-xs italic text-[#C46829]">Brooklyn Heights tours frequently book in advance</span>
                    )}
                  </div>
                )}
              </div>
            </CardFooter>
          </Card>
        );
      })}
    </div>
  );
};

export default TourList;
